import type { ConfigurationResponse, HotelsResponse } from "./types";

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null;
}

export type MewsCategoryBedMaps = {
  /** NormalBedCount + ExtraBedCount per Mews room category id */
  totalByCategoryId: ReadonlyMap<string, number>;
  /** NormalBedCount only (for display) */
  normalByCategoryId: ReadonlyMap<string, number>;
  /** ExtraBedCount per Mews room category id (0 when configured with no extra beds) */
  extraByCategoryId: ReadonlyMap<string, number>;
};

/**
 * Merges NormalBedCount / ExtraBedCount from an array of Mews room category objects
 * (same shape as `Enterprise.Categories[]` or `hotels.RoomCategories[]`).
 * Later merges overwrite earlier entries for the same `Id`.
 */
function mergeBedCountsFromRoomCategoryList(
  list: unknown,
  totalByCategoryId: Map<string, number>,
  normalByCategoryId: Map<string, number>,
  extraByCategoryId: Map<string, number>,
): void {
  if (!Array.isArray(list)) return;

  for (const raw of list) {
    if (!isRecord(raw)) continue;
    const id = raw["Id"];
    if (typeof id !== "string" || !id) continue;

    const nRaw = raw["NormalBedCount"];
    const eRaw = raw["ExtraBedCount"];
    if (typeof nRaw !== "number" && typeof eRaw !== "number") continue;

    const n = typeof nRaw === "number" && Number.isFinite(nRaw) ? nRaw : 0;
    const e = typeof eRaw === "number" && Number.isFinite(eRaw) ? eRaw : 0;
    totalByCategoryId.set(id, n + e);
    extraByCategoryId.set(id, e);

    if (typeof nRaw === "number" && Number.isFinite(nRaw)) {
      normalByCategoryId.set(id, nRaw);
    }
  }
}

/**
 * Reads bed counts from distributor getConfiguration, then overlays `hotels/get` `RoomCategories`
 * when provided. Configuration sometimes omits or zeroes `ExtraBedCount` while hotels returns it.
 */
export function buildMewsCategoryBedMaps(
  configuration: ConfigurationResponse | null | undefined,
  hotels?: HotelsResponse | null,
): MewsCategoryBedMaps {
  const totalByCategoryId = new Map<string, number>();
  const normalByCategoryId = new Map<string, number>();
  const extraByCategoryId = new Map<string, number>();

  const configs = configuration?.Configurations;
  if (Array.isArray(configs)) {
    for (const cfg of configs) {
      if (!isRecord(cfg)) continue;
      const ent = cfg["Enterprise"];
      if (!isRecord(ent)) continue;
      const cats = ent["Categories"];
      mergeBedCountsFromRoomCategoryList(
        cats,
        totalByCategoryId,
        normalByCategoryId,
        extraByCategoryId,
      );
    }
  }

  mergeBedCountsFromRoomCategoryList(
    hotels?.RoomCategories,
    totalByCategoryId,
    normalByCategoryId,
    extraByCategoryId,
  );

  return { totalByCategoryId, normalByCategoryId, extraByCategoryId };
}

/**
 * Mews room category id → NormalBedCount + ExtraBedCount (used for occupancy filtering).
 */
export function buildMewsCategoryBedCapacityMap(
  configuration: ConfigurationResponse | null | undefined,
  hotels?: HotelsResponse | null,
): ReadonlyMap<string, number> {
  return buildMewsCategoryBedMaps(configuration, hotels).totalByCategoryId;
}

const JUNIOR_SUITE_LOCAL_ROOM_ID = "junior-suite";

type BookPageBedMaps = Pick<MewsCategoryBedMaps, "normalByCategoryId" | "extraByCategoryId">;

/**
 * Book page filter/sort capacity: Junior Suite uses base + extra beds; all other rooms use base only.
 */
export function getBookPageOccupancyCapacity(
  localRoomId: string,
  mewsCategoryId: string | null | undefined,
  beds: BookPageBedMaps,
  fallbackMaxOccupancy: number,
): number {
  if (!mewsCategoryId) return fallbackMaxOccupancy;
  const normal = beds.normalByCategoryId.get(mewsCategoryId);
  if (normal == null) return fallbackMaxOccupancy;
  if (localRoomId === JUNIOR_SUITE_LOCAL_ROOM_ID) {
    const extra = beds.extraByCategoryId.get(mewsCategoryId) ?? 0;
    return normal + extra;
  }
  return normal;
}

/**
 * Book page occupancy icon label: Junior Suite shows "N+E" when extra beds exist; others show base only.
 */
export function getBookPageOccupancyDisplay(
  localRoomId: string,
  mewsCategoryId: string | null | undefined,
  beds: BookPageBedMaps,
  fallbackMaxOccupancy: number,
): string {
  if (!mewsCategoryId) return String(fallbackMaxOccupancy);
  const normal = beds.normalByCategoryId.get(mewsCategoryId);
  if (normal == null) return String(fallbackMaxOccupancy);
  if (localRoomId === JUNIOR_SUITE_LOCAL_ROOM_ID) {
    const extra = beds.extraByCategoryId.get(mewsCategoryId) ?? 0;
    return extra > 0 ? `${normal}+${extra}` : String(normal);
  }
  return String(normal);
}

/** Sort rank for occupancy fit: exact X first, then X+1, X+2…; undersized rooms last. */
export function bookPageOccupancyFitRank(capacity: number, guestCount: number): number {
  const delta = capacity - guestCount;
  return delta >= 0 ? delta : 1_000_000 + Math.abs(delta);
}
